import React, { createContext, useEffect, useState } from "react";
import { AuthContextProps } from "../types";
import authVersionService from "../services/authVersionService";
import useTokenExpiryWarning from "@/hooks/useTokenExpiryWarning";
import useTrackPromoVisit from "@/hooks/useTrackPromoVisit";
import {
  ACK_USER_SIGN_OUT,
  PostSessionExpiredMsg,
  TOKEN_REFRESH,
} from "@/services/PostMessageService";
import httpClient from "@/api/httpClient";
import AuthService from "@/services/AuthService";
import { setupAuthRequestInterceptor } from "../authRequestInterceptors";
import UnauthorizedPage from "@/components/UnauthorizedPage";
import { safeLocalStorage } from "@/utils";
import ConfigService from "@/services/ConfigService";

const AuthContext = createContext<AuthContextProps>({
  isLoggedIn: false,
  getCurrentUser: null,
  token: null,
});

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authState, setAuthState] = useState<AuthContextProps>({
    isLoggedIn: false,
    getCurrentUser: null,
    token: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isForbidden, setIsForbidden] = useState(false);

  useTokenExpiryWarning(authState.token);
  useTrackPromoVisit(authState, ConfigService.getPromoId());

  const handleSetUserData = (data: AuthContextProps | null) => {
    if (data) {
      setAuthState(data);
    } else {
      setAuthState({
        isLoggedIn: false,
        getCurrentUser: null,
        token: null,
      });
      authVersionService.clearHeaders();
    }
  };

  const handleSignOut = () => {
    handleSetUserData(null);
    safeLocalStorage.removeItem("user");
  };

  const handleUnauthorized = () => {
    handleSignOut();
    setIsUnauthorized(true);
    window.parent.postMessage(PostSessionExpiredMsg(), "*");
  };

  const handleForbidden = () => {
    setIsForbidden(true);
    window.parent.postMessage(PostSessionExpiredMsg("403_forbidden"), "*");
  };

  const handleAuth = async () => {
    try {
      const token = AuthService.getToken();

      let res;
      if (token) {
        res = await authVersionService.authenticateWithToken({ token, handleUnauthorized });
      } else {
        res = await authVersionService.authenticate();
      }

      handleSetUserData({
        isLoggedIn: true,
        getCurrentUser: res?.user || res?.getCurrentUser,
        token: res?.token,
      });
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        handleSignOut();
        console.warn("Unauthorized request. Please login again.", error.response.data);
      } else if (error.response && error.response.status === 403) {
        handleForbidden();
        console.warn("Forbidden request. User does not have permission.", error.response.data);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTokenRefresh = async (newToken: string) => {
    try {
      safeLocalStorage.removeItem("user");
      const res = await authVersionService.authenticateWithToken({
        token: newToken,
        handleUnauthorized,
      });
      handleSetUserData({
        isLoggedIn: true,
        getCurrentUser: res?.user || res?.getCurrentUser,
        token: res?.token,
      });
    } catch (error: any) {
      console.error("Token refresh failed.", error);
    }
  };

  useEffect(() => {
    handleAuth();

    const requestInterceptor = setupAuthRequestInterceptor();
    const interceptor = httpClient.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error?.response?.status === 401) {
          handleUnauthorized();
        } else if (error?.response?.status === 403) {
          const requestUrl = error?.config?.url || "";
          if (requestUrl.includes("/auth")) {
            handleForbidden();
          }
        }
        return Promise.reject(error);
      }
    );

    const handleMessage = (message: MessageEvent) => {
      if (!message.data || typeof message.data !== "object" || !message.data.event) {
        return;
      }

      switch (message.data.event) {
        case ACK_USER_SIGN_OUT:
          handleSignOut();
          break;
        case TOKEN_REFRESH: {
          const newToken = message?.data?.data?.token;
          if (!newToken) break;
          handleTokenRefresh(newToken);
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      httpClient.interceptors.request.eject(requestInterceptor);
      httpClient.interceptors.response.eject(interceptor);
      window.removeEventListener("message", handleMessage);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isUnauthorized && authState?.isLoggedIn) {
      setIsUnauthorized(false);
    }
  }, [authState, isUnauthorized]);

  if (isLoading) {
    return null;
  }

  if (isUnauthorized && !authState?.isLoggedIn) {
    return <UnauthorizedPage />;
  }

  return (
    <AuthContext.Provider value={{ ...authState, isLoading, isForbidden }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
